import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { SupabaseSlackAliasService } from "../../../../slack/server/slack-supabase-service";
import { slackAliasSchema } from "../../types";
import { SlackAliasImageUpload } from "./alias-image-upload";

import type {
  SlackAlias,
  SlackAliasWithDefault,
} from "../../../../slack/server/slack-supabase-service";
import type { SlackAliasForm as SlackAliasFormType } from "../../types";
import type { FC } from "react";

export const SlackAliasForm: FC<{
  onComplete: (alias: SlackAlias) => void;
  open: boolean;
  close: () => void;
  existing?: SlackAliasWithDefault;
}> = ({ onComplete, existing, open, close }) => {
  const [saving, setSaving] = useState(false);

  const methods = useForm({
    defaultValues: { name: existing?.name ?? "", image: existing?.image ?? "" },
    resolver: yupResolver(slackAliasSchema),
  });

  const { reset } = methods;

  const onSubmit = useSupabaseCallback(
    async ({ supabase }, { name, image }: SlackAliasFormType): Promise<void> => {
      setSaving(true);

      const service = new SupabaseSlackAliasService(supabase);

      const { data, error } = await (existing && existing.id
        ? service.update(existing.id, { name, image })
        : service.insert({ name, image }));

      setSaving(false);

      if (error || !data) {
        toast.error(existing ? "Failed to update alias" : "Failed to create alias");
        return;
      }

      toast.success(existing ? "Alias Updated" : "Alias created");
      onComplete(data);
      close();
      reset();
    },
    [onComplete, existing, close, reset],
  );

  return (
    <Dialog open={open} onClose={() => !saving && close()}>
      <DialogTitle>{existing ? "Update Slack Alias" : "Add Slack Alias"}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Set a name and image for your alias. Note that images will be automatically resized to fit
          Slack specifications. We recommend using a square image between 512x512 and 1024x1024.
        </DialogContentText>
        <FormProvider {...methods}>
          <form id="slack-alias-create-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <Controller
              name="name"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  margin="dense"
                  id="name"
                  label="Name"
                  type="text"
                  fullWidth
                  variant="standard"
                  value={field.value}
                  placeholder="e.g. Holiday Bot"
                  onChange={field.onChange}
                  helperText={error?.message}
                  error={Boolean(error)}
                />
              )}
            />
            <SlackAliasImageUpload />
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id={`slack-alias-dialog-${existing ? "update" : "create"}-add`}
          disabled={saving}
          type="submit"
          form="slack-alias-create-form"
        >
          {saving ? <CircularProgress size={20} /> : existing ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
